import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { CATEGORIES, IMPORTANCE, STATUSES } from "@/lib/content";
import { Plus, Trash2, ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";

const EMPTY = {
  title: "", category: "ia", importance: "a-connaitre", status: "brouillon",
  summary: "", image: "", companies_concerned: "", verified_fact: "", what_changes: "",
  risks: "", opportunities: "", points_to_verify: "", analysis: "", sources: [],
};

const Text = ({ label, value, onChange, testid, placeholder }) => (
  <label className="block">
    <span className="bz-label text-bz-slate">{label}</span>
    <input value={value || ""} onChange={onChange} data-testid={testid} placeholder={placeholder}
      className="mt-2 w-full border border-bz-line bg-bz-ivory px-4 py-3 text-sm text-bz-navy outline-none focus:border-bz-petrol" />
  </label>
);

const Area = ({ label, value, onChange, testid, rows = 4 }) => (
  <label className="block">
    <span className="bz-label text-bz-slate">{label}</span>
    <textarea value={value || ""} onChange={onChange} data-testid={testid} rows={rows}
      className="mt-2 w-full border border-bz-line bg-bz-ivory px-4 py-3 text-sm leading-relaxed text-bz-navy outline-none focus:border-bz-petrol" />
  </label>
);

export default function ArticleEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(!!id);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  useEffect(() => {
    if (id) {
      api.get(`/admin/articles/${id}`).then(({ data }) => setForm(data)).catch(() => setError("Article introuvable.")).finally(() => setLoading(false));
    }
  }, [id]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const setSource = (i, key) => (e) => {
    const sources = [...(form.sources || [])];
    sources[i] = { ...sources[i], [key]: e.target.value };
    setForm((f) => ({ ...f, sources }));
  };
  const addSource = () => setForm((f) => ({ ...f, sources: [...(f.sources || []), { label: "", url: "" }] }));
  const removeSource = (i) => setForm((f) => ({ ...f, sources: f.sources.filter((_, idx) => idx !== i) }));

  const persist = async (overrideStatus) => {
    setError(""); setNotice(""); setSaving(true);
    const payload = { ...form, sources: (form.sources || []).filter((s) => s.label && s.url) };
    if (overrideStatus) payload.status = overrideStatus;
    try {
      let saved;
      if (id) {
        ({ data: saved } = await api.put(`/admin/articles/${id}`, payload));
      } else {
        ({ data: saved } = await api.post("/admin/articles", payload));
      }
      setForm(saved);
      setNotice("Enregistré.");
      if (!id) navigate(`/admin/articles/${saved.id}/edit`, { replace: true });
      return saved;
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Enregistrement impossible.");
      return null;
    } finally {
      setSaving(false);
    }
  };

  const saveAndPreview = async () => {
    const saved = await persist();
    if (saved) navigate(`/admin/articles/${saved.id}/preview`);
  };

  if (loading) return <p className="text-bz-slate">Chargement…</p>;

  return (
    <div className="max-w-3xl" data-testid="admin-article-editor">
      <Link to="/admin/articles" className="inline-flex items-center gap-2 text-sm text-bz-slate hover:text-bz-navy">
        <ArrowLeft size={16} strokeWidth={1.5} /> Retour à la liste
      </Link>
      <h1 className="mt-6 font-serif text-3xl text-bz-navy">{id ? "Modifier le décryptage" : "Nouveau décryptage"}</h1>

      {error && <p className="mt-4 text-sm text-red-600" data-testid="editor-error">{error}</p>}
      {notice && <p className="mt-4 text-sm text-bz-petrol" data-testid="editor-notice">{notice}</p>}

      <div className="mt-8 space-y-6">
        <Text label="Titre" value={form.title} onChange={set("title")} testid="editor-title" />

        <div className="grid grid-cols-1 gap-6 sm:grid-cols-3">
          <label className="block">
            <span className="bz-label text-bz-slate">Catégorie</span>
            <select value={form.category} onChange={set("category")} data-testid="editor-category" className="mt-2 w-full border border-bz-line bg-bz-ivory px-3 py-3 text-sm text-bz-navy outline-none focus:border-bz-petrol">
              {CATEGORIES.map((c) => <option key={c.slug} value={c.slug}>{c.short}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="bz-label text-bz-slate">Importance</span>
            <select value={form.importance} onChange={set("importance")} data-testid="editor-importance" className="mt-2 w-full border border-bz-line bg-bz-ivory px-3 py-3 text-sm text-bz-navy outline-none focus:border-bz-petrol">
              {IMPORTANCE.map((i) => <option key={i.slug} value={i.slug}>{i.label}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="bz-label text-bz-slate">Statut</span>
            <select value={form.status} onChange={set("status")} data-testid="editor-status" className="mt-2 w-full border border-bz-line bg-bz-ivory px-3 py-3 text-sm text-bz-navy outline-none focus:border-bz-petrol">
              {STATUSES.map((s) => <option key={s.slug} value={s.slug}>{s.label}</option>)}
            </select>
          </label>
        </div>

        <Text label="Image (URL, facultatif)" value={form.image} onChange={set("image")} testid="editor-image" placeholder="https://…" />
        <Area label="Résumé (3 à 5 lignes)" value={form.summary} onChange={set("summary")} testid="editor-summary" rows={3} />
        <Area label="Entreprises concernées" value={form.companies_concerned} onChange={set("companies_concerned")} testid="editor-companies" rows={2} />
        <Area label="Le fait vérifié" value={form.verified_fact} onChange={set("verified_fact")} testid="editor-verified" />
        <Area label="Ce qui change concrètement" value={form.what_changes} onChange={set("what_changes")} testid="editor-changes" />
        <Area label="Risques" value={form.risks} onChange={set("risks")} testid="editor-risks" />
        <Area label="Opportunités" value={form.opportunities} onChange={set("opportunities")} testid="editor-opportunities" />
        <Area label="Points à vérifier" value={form.points_to_verify} onChange={set("points_to_verify")} testid="editor-points" />
        <Area label="Analyse & mise en perspective BENZENATI" value={form.analysis} onChange={set("analysis")} testid="editor-analysis" rows={6} />

        <div>
          <div className="flex items-center justify-between">
            <span className="bz-label text-bz-slate">Sources officielles</span>
            <button onClick={addSource} data-testid="editor-add-source" className="inline-flex items-center gap-1.5 text-xs text-bz-petrol">
              <Plus size={14} strokeWidth={1.8} /> Ajouter une source
            </button>
          </div>
          <div className="mt-3 space-y-3">
            {(form.sources || []).map((s, i) => (
              <div key={i} className="flex flex-col gap-2 border border-bz-line bg-white p-3 sm:flex-row" data-testid={`editor-source-${i}`}>
                <input value={s.label || ""} onChange={setSource(i, "label")} placeholder="Intitulé" data-testid={`source-label-${i}`} className="flex-1 border border-bz-line bg-bz-ivory px-3 py-2 text-sm outline-none focus:border-bz-petrol" />
                <input value={s.url || ""} onChange={setSource(i, "url")} placeholder="https://…" data-testid={`source-url-${i}`} className="flex-1 border border-bz-line bg-bz-ivory px-3 py-2 text-sm outline-none focus:border-bz-petrol" />
                <button onClick={() => removeSource(i)} className="text-bz-slate hover:text-red-600" title="Retirer"><Trash2 size={16} strokeWidth={1.5} /></button>
              </div>
            ))}
            {(form.sources || []).length === 0 && <p className="text-sm text-bz-slate/70">Aucune source ajoutée.</p>}
          </div>
        </div>
      </div>

      <div className="sticky bottom-0 mt-10 flex flex-wrap gap-3 border-t border-bz-line bg-bz-ivory py-5">
        <button onClick={() => persist()} disabled={saving} data-testid="editor-save" className="bg-bz-navy px-6 py-3 text-xs uppercase tracking-[0.15em] text-bz-ivory transition-colors hover:bg-bz-petrol disabled:opacity-60">
          {saving ? "Enregistrement…" : "Enregistrer"}
        </button>
        <button onClick={saveAndPreview} disabled={saving} data-testid="editor-preview" className="border border-bz-navy px-6 py-3 text-xs uppercase tracking-[0.15em] text-bz-navy transition-colors hover:bg-bz-navy hover:text-bz-ivory disabled:opacity-60">
          Enregistrer & prévisualiser
        </button>
        {form.status !== "publie" ? (
          <button onClick={() => persist("publie")} disabled={saving} data-testid="editor-publish" className="bg-bz-petrol px-6 py-3 text-xs uppercase tracking-[0.15em] text-white transition-colors hover:bg-bz-navy disabled:opacity-60">
            Publier
          </button>
        ) : (
          <button onClick={() => persist("valide")} disabled={saving} data-testid="editor-unpublish" className="border border-bz-amber px-6 py-3 text-xs uppercase tracking-[0.15em] text-bz-amber transition-colors hover:bg-bz-amber hover:text-bz-navy disabled:opacity-60">
            Dépublier
          </button>
        )}
      </div>
    </div>
  );
}
